import { useEffect, useCallback } from "react";

/**
 * Hook to handle page unload events and flush pending operations
 * Prevents loss of cart operations when user navigates away or refreshes
 */
export function usePageUnloadHandler(
  flushPendingUpdates: () => Promise<void>,
  hasPendingUpdates: () => boolean
) {
  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (hasPendingUpdates()) {
        // Show warning to user before leaving with pending changes
        const message =
          "Tienes cambios pendientes en tu carrito. ¿Seguro que quieres salir?";
        event.preventDefault();
        event.returnValue = message;

        // Try to flush updates quickly
        flushPendingUpdates().catch(console.error);

        return message;
      }
    },
    [flushPendingUpdates, hasPendingUpdates]
  );

  const handleVisibilityChange = useCallback(() => {
    // When page becomes hidden (user switching tabs/minimizing), flush updates
    if (document.hidden && hasPendingUpdates()) {
      console.log("🔄 Page hidden, flushing pending cart updates...");
      flushPendingUpdates().catch(console.error);
    }
  }, [flushPendingUpdates, hasPendingUpdates]);

  useEffect(() => {
    // Listen for page unload events
    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleBeforeUnload, handleVisibilityChange]);
}
